/**
 * Sysadmin Models — Trust Toggle Authoring (Wave 3 / M6c criterion 5).
 *
 * The Trust toggle is the ONLY tenant-facing wrapper-config surface.
 * Per I-M6.7, tenants don't see wrapper functions, source code, chain
 * positions, or anything sysadmin-internal — they see Trust toggles
 * (label + description + on/off) that the engineering team explicitly
 * exposes.
 *
 * This page is where the engineering team authors those toggles:
 *   - config_key (must match `^trust\.[a-z][a-z0-9_]*$`; immutable)
 *   - label (tenant-visible)
 *   - description (tenant-visible)
 *   - default state (on/off — applied per-tenant on first read)
 *   - applies_to_brands (NULL = global; or specific brand IDs)
 *   - wrapper_function_id (immutable; the underlying filter)
 *   - sort_order (display order in the tenant Trust UI)
 *   - is_active (retire workflow)
 *
 * Layout:
 *   - Table of all toggles with badge showing default state + applies-to
 *   - "+ New" dialog: RHF + Zod form with tenant-preview panel showing
 *     how the toggle will look in the Trust UI
 *   - Per-row inline edit dialog (label/description/defaultState/
 *     appliesToBrands/sortOrder editable; configKey + wrapperFunctionId
 *     immutable post-create — changing them silently re-meanings the
 *     toggle for tenants who already opted in)
 *   - Retire (soft) — alert dialog with usage warning
 *
 * Per I-M6.4 RBAC: write:argus.trust at the resource layer;
 * platform.ts already gates the parent under requireSysadmin.
 *
 * See:
 *   - docs/waves/wave-3/M6C-MODELS-UI-ANCHOR.md
 *   - docs/decisions/0020-*.md (architecture lock)
 *   - packages/api/src/routes/v1/admin/argus-models.ts (POST + PATCH)
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ToggleRight,
  Plus,
  Search,
  Eye,
  PenSquare,
  PowerOff,
  Power,
  Loader2,
  Globe,
  Boxes,
  ChevronRight,
  CheckCircle2,
  Circle,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useTrustToggles,
  useArgusModels,
  useWrapperFunctions,
  useCreateTrustToggle,
  useUpdateTrustToggle,
  type TrustToggle,
  type WrapperFunction,
} from '@/lib/use-argus-models';
import { ModelsSubNav } from './models/index';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/models/trust-toggles')({
  component: TrustTogglesPage,
});

// ─── Schemas ──────────────────────────────────────────────────────────────

const CreateSchema = z.object({
  configKey: z
    .string()
    .min(7, 'Required')
    .max(120)
    .regex(
      /^trust\.[a-z][a-z0-9_]*$/,
      'Must start with `trust.` followed by lowercase letters / digits / underscore',
    ),
  label: z.string().min(1, 'Tenant-visible label required').max(120),
  description: z.string().min(1, 'Tenant-visible description required'),
  defaultState: z.boolean(),
  wrapperFunctionId: z.string().uuid('Pick a wrapper function'),
  appliesToBrands: z.array(z.string()).nullable(),
  sortOrder: z.number().int().min(0).max(10000),
});

type CreateValues = z.infer<typeof CreateSchema>;

const EditSchema = z.object({
  label: z.string().min(1).max(120),
  description: z.string().min(1),
  defaultState: z.boolean(),
  appliesToBrands: z.array(z.string()).nullable(),
  sortOrder: z.number().int().min(0).max(10000),
  isActive: z.boolean(),
});

type EditValues = z.infer<typeof EditSchema>;

// ─── Page ─────────────────────────────────────────────────────────────────

function TrustTogglesPage() {
  const [searchInput, setSearchInput] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TrustToggle | null>(null);
  const [retiring, setRetiring] = useState<TrustToggle | null>(null);

  const togglesQuery = useTrustToggles();
  const fnsQuery = useWrapperFunctions();
  const brandsQuery = useArgusModels();

  const filtered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    return (togglesQuery.data?.items ?? []).filter((t) => {
      if (!showRetired && !t.isActive) return false;
      if (!q) return true;
      return (
        t.configKey.toLowerCase().includes(q) ||
        t.label.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q)
      );
    });
  }, [togglesQuery.data, searchInput, showRetired]);

  // Only toggleable (non-audit) functions are eligible for new Trust toggles.
  // appliesTo = 'both' covers either chain side. category = 'audit' is
  // never tenant-toggleable because audit emit is mandatory by spec.
  const eligibleFunctions = useMemo(() => {
    return (fnsQuery.data?.items ?? []).filter(
      (f) => f.isActive && f.category !== 'audit',
    );
  }, [fnsQuery.data]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ToggleRight className="h-6 w-6" />
            Trust Toggles
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            The only tenant-facing wrapper-config surface. Sysadmin authors the
            label, description, and default state; tenants flip the toggle on/off
            in their Trust module. Per I-M6.7 tenants never see the underlying
            wrapper function or chain — only what's defined here.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4 mr-1" />
          New trust toggle
        </Button>
      </div>

      <ModelsSubNav active="/sysadmin/models/trust-toggles" />

      {/* Filter bar */}
      <Card>
        <CardContent className="py-4 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by config key, label, or description…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <label className="flex items-center gap-2 text-sm shrink-0 cursor-pointer">
            <Switch checked={showRetired} onCheckedChange={setShowRetired} />
            <span>Show retired</span>
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {togglesQuery.error ? (
            <div className="p-8 text-center text-sm text-destructive">
              Failed to load: {togglesQuery.error.message}
            </div>
          ) : togglesQuery.isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground animate-pulse">
              Loading toggles…
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              hasFilters={searchInput.length > 0 || showRetired}
              onCreate={() => setCreating(true)}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Toggle</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Applies to</TableHead>
                  <TableHead>Wired function</TableHead>
                  <TableHead className="text-right">Sort</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} className={cn(!t.isActive && 'opacity-60')}>
                    <TableCell className="max-w-md">
                      <div className="font-medium">{t.label}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">
                        {t.configKey}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {t.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      {t.defaultState ? (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-300 gap-1 text-[10px]">
                          <CheckCircle2 className="h-3 w-3" />
                          on
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground gap-1 text-[10px]">
                          <Circle className="h-3 w-3" />
                          off
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {t.appliesToBrands === null || t.appliesToBrands.length === 0 ? (
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          <Globe className="h-3 w-3" />
                          all brands
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-1 flex-wrap max-w-[180px]">
                          {t.appliesToBrands.slice(0, 3).map((b) => (
                            <Badge key={b} variant="outline" className="text-[10px] font-mono">
                              {b}
                            </Badge>
                          ))}
                          {t.appliesToBrands.length > 3 && (
                            <Badge variant="outline" className="text-[10px]">
                              +{t.appliesToBrands.length - 3} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-xs font-mono text-muted-foreground">
                        {t.wrapperFunctionKey}
                      </code>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {t.sortOrder}
                    </TableCell>
                    <TableCell>
                      {t.isActive ? (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">
                          active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground text-[10px]">
                          retired
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(t)}
                          className="h-7 px-2"
                          title="Edit"
                        >
                          <PenSquare className="h-3.5 w-3.5" />
                        </Button>
                        {t.isActive ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRetiring(t)}
                            className="h-7 px-2 text-amber-700 hover:text-amber-800"
                            title="Retire"
                          >
                            <PowerOff className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing(t)}
                            className="h-7 px-2 text-emerald-700 hover:text-emerald-800"
                            title="Re-activate (via edit dialog)"
                          >
                            <Power className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {!togglesQuery.isLoading && togglesQuery.data && (
        <div className="text-sm text-muted-foreground flex items-center justify-between">
          <span>
            {filtered.length} of {togglesQuery.data.items.length} toggles match
          </span>
          <span>
            {togglesQuery.data.items.filter((t) => t.isActive).length} active /{' '}
            {togglesQuery.data.items.length} total
          </span>
        </div>
      )}

      <CreateTrustToggleDialog
        open={creating}
        onOpenChange={setCreating}
        functions={eligibleFunctions}
        brands={brandsQuery.data?.items ?? []}
      />

      <EditTrustToggleDialog
        toggle={editing}
        onClose={() => setEditing(null)}
        functions={fnsQuery.data?.items ?? []}
        brands={brandsQuery.data?.items ?? []}
      />

      <RetireConfirm
        toggle={retiring}
        onClose={() => setRetiring(null)}
      />
    </div>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────

function CreateTrustToggleDialog({
  open,
  onOpenChange,
  functions,
  brands,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  functions: WrapperFunction[];
  brands: Array<{ brandId: string; displayName: string }>;
}) {
  const createMutation = useCreateTrustToggle();

  const form = useForm<CreateValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(CreateSchema as any),
    mode: 'onChange',
    defaultValues: {
      configKey: 'trust.',
      label: '',
      description: '',
      defaultState: false,
      wrapperFunctionId: '',
      appliesToBrands: null,
      sortOrder: 100,
    },
  });

  // Tenant-preview reflects current form state in real time.
  const previewLabel = form.watch('label') || 'Untitled toggle';
  const previewDescription = form.watch('description') || 'No description yet.';
  const previewDefault = form.watch('defaultState');

  const onSubmit = async (values: CreateValues) => {
    try {
      await createMutation.mutateAsync({
        configKey: values.configKey,
        label: values.label,
        description: values.description,
        defaultState: values.defaultState,
        wrapperFunctionId: values.wrapperFunctionId,
        appliesToBrands: values.appliesToBrands,
        sortOrder: values.sortOrder,
      });
      form.reset();
      onOpenChange(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (/config_key_taken|configkey/i.test(message)) {
        form.setError('configKey', { type: 'server', message: 'A toggle with this key already exists' });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) form.reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            New Trust Toggle
          </DialogTitle>
          <DialogDescription>
            Define what tenants see in their Trust UI and which wrapper function it controls.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Form */}
            <div className="space-y-4">
              <Field
                label="Config key"
                hint="Lowercase. Must start with `trust.`. Immutable post-create."
                error={form.formState.errors.configKey?.message}
              >
                <Input
                  {...form.register('configKey')}
                  className="font-mono"
                  placeholder="trust.my_toggle"
                />
              </Field>

              <Field
                label="Tenant-visible label"
                hint="Shown in the Trust UI. Keep it short and human."
                error={form.formState.errors.label?.message}
              >
                <Input
                  {...form.register('label')}
                  placeholder="PII detection"
                />
              </Field>

              <Field
                label="Tenant-visible description"
                hint="A 1-2 sentence explanation tenants read before flipping the toggle."
                error={form.formState.errors.description?.message}
              >
                <Textarea
                  {...form.register('description')}
                  rows={3}
                  placeholder="Detect and redact personal data (names, emails, SSN) in inputs and outputs."
                />
              </Field>

              <div className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <div className="text-sm font-medium">Default state</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    What tenants see before they touch it.
                  </div>
                </div>
                <Switch
                  checked={form.watch('defaultState')}
                  onCheckedChange={(v) => form.setValue('defaultState', v)}
                />
              </div>

              <Field
                label="Wired wrapper function"
                hint="The filter that runs when a tenant has this toggle on. Immutable post-create."
                error={form.formState.errors.wrapperFunctionId?.message}
              >
                <Select
                  value={form.watch('wrapperFunctionId')}
                  onValueChange={(v) => form.setValue('wrapperFunctionId', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a function from the library…" />
                  </SelectTrigger>
                  <SelectContent>
                    {functions.length === 0 ? (
                      <div className="text-xs text-muted-foreground px-3 py-2">
                        No eligible functions — author one in the Functions tab first.
                      </div>
                    ) : (
                      functions.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{f.displayName}</span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {f.functionKey}
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </Field>

              <BrandSelector
                value={form.watch('appliesToBrands')}
                onChange={(v) => form.setValue('appliesToBrands', v)}
                brands={brands}
              />

              <Field label="Sort order" hint="Lower values appear higher in the tenant Trust UI.">
                <Input
                  type="number"
                  min="0"
                  {...form.register('sortOrder', { valueAsNumber: true })}
                />
              </Field>
            </div>

            {/* Tenant preview */}
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Eye className="h-3 w-3" />
                Tenant preview
              </div>
              <Card className="bg-muted/20">
                <CardContent className="py-4">
                  <TenantTogglePreview
                    label={previewLabel}
                    description={previewDescription}
                    defaultState={previewDefault}
                  />
                </CardContent>
              </Card>
              <div className="text-xs text-muted-foreground flex items-start gap-1.5 pt-1">
                <Info className="h-3 w-3 shrink-0 mt-0.5" />
                <span>
                  This is what the toggle will look like in the tenant's Trust module.
                  Per I-M6.7, the wrapper function it controls is never shown to tenants.
                </span>
              </div>
            </div>
          </div>

          {createMutation.isError && (
            <Card className="border-destructive">
              <CardContent className="py-3 text-sm text-destructive">
                {createMutation.error.message}
              </CardContent>
            </Card>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Create toggle
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit dialog ──────────────────────────────────────────────────────────

function EditTrustToggleDialog({
  toggle,
  onClose,
  functions,
  brands,
}: {
  toggle: TrustToggle | null;
  onClose: () => void;
  functions: WrapperFunction[];
  brands: Array<{ brandId: string; displayName: string }>;
}) {
  const updateMutation = useUpdateTrustToggle();

  const form = useForm<EditValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(EditSchema as any),
    mode: 'onChange',
    values: toggle
      ? {
          label: toggle.label,
          description: toggle.description,
          defaultState: toggle.defaultState,
          appliesToBrands: toggle.appliesToBrands,
          sortOrder: toggle.sortOrder,
          isActive: toggle.isActive,
        }
      : undefined,
  });

  if (!toggle) return null;

  const wiredFunction = functions.find((f) => f.id === toggle.wrapperFunctionId);

  const onSubmit = async (values: EditValues) => {
    await updateMutation.mutateAsync({
      id: toggle.id,
      label: values.label,
      description: values.description,
      defaultState: values.defaultState,
      appliesToBrands: values.appliesToBrands,
      sortOrder: values.sortOrder,
      isActive: values.isActive,
    });
    onClose();
  };

  return (
    <Dialog open={!!toggle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenSquare className="h-5 w-5" />
            Edit {toggle.label}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{toggle.configKey}</DialogDescription>
        </DialogHeader>

        {/* Immutable fields callout */}
        <Card className="bg-muted/30 border-dashed">
          <CardContent className="py-3 text-xs space-y-1">
            <div className="flex items-center gap-2">
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">Immutable post-create</span>
            </div>
            <div className="text-muted-foreground">
              Config key:{' '}
              <code className="font-mono">{toggle.configKey}</code>
            </div>
            <div className="text-muted-foreground">
              Wired function:{' '}
              <code className="font-mono">
                {wiredFunction?.displayName ?? toggle.wrapperFunctionKey}
              </code>
              {wiredFunction && !wiredFunction.isActive && (
                <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300 text-[10px]">
                  function retired
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Field label="Tenant-visible label" error={form.formState.errors.label?.message}>
            <Input {...form.register('label')} />
          </Field>

          <Field label="Tenant-visible description" error={form.formState.errors.description?.message}>
            <Textarea {...form.register('description')} rows={3} />
          </Field>

          <div className="flex items-center justify-between p-3 border rounded-md">
            <div>
              <div className="text-sm font-medium">Default state</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Changing this affects new tenants only — existing tenants who already
                set their preference are not overwritten.
              </div>
            </div>
            <Switch
              checked={form.watch('defaultState')}
              onCheckedChange={(v) => form.setValue('defaultState', v)}
            />
          </div>

          <BrandSelector
            value={form.watch('appliesToBrands')}
            onChange={(v) => form.setValue('appliesToBrands', v)}
            brands={brands}
          />

          <Field label="Sort order">
            <Input type="number" min="0" {...form.register('sortOrder', { valueAsNumber: true })} />
          </Field>

          <div className="flex items-center justify-between p-3 border rounded-md">
            <div>
              <div className="text-sm font-medium">Active</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Toggle off to retire (soft). Tenants who already opted in retain
                their preference; the wrapper-resolver simply skips this toggle.
              </div>
            </div>
            <Switch
              checked={form.watch('isActive')}
              onCheckedChange={(v) => form.setValue('isActive', v)}
            />
          </div>

          {updateMutation.isError && (
            <Card className="border-destructive">
              <CardContent className="py-3 text-sm text-destructive">
                {updateMutation.error.message}
              </CardContent>
            </Card>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Retire confirm ───────────────────────────────────────────────────────

function RetireConfirm({
  toggle,
  onClose,
}: {
  toggle: TrustToggle | null;
  onClose: () => void;
}) {
  const updateMutation = useUpdateTrustToggle();

  if (!toggle) return null;

  return (
    <AlertDialog open={!!toggle} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <PowerOff className="h-5 w-5 text-amber-700" />
            Retire {toggle.label}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Retiring sets <code>is_active = false</code>. The toggle row stays in
                the catalog so tenants who already opted in keep their preference, but
                the wrapper-resolver will not include this toggle in any new chain.
              </p>
              <p className="text-muted-foreground text-xs">
                You can re-activate later via the edit dialog. To rewire the toggle
                to a different wrapper function, retire this toggle and create a new
                one — that's the supported path because changing the wired function
                silently re-meanings the toggle for opted-in tenants.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={updateMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={updateMutation.isPending}
            onClick={async (e) => {
              e.preventDefault();
              await updateMutation.mutateAsync({ id: toggle.id, isActive: false });
              onClose();
            }}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Retire toggle
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Brand selector ───────────────────────────────────────────────────────

function BrandSelector({
  value,
  onChange,
  brands,
}: {
  value: string[] | null;
  onChange: (v: string[] | null) => void;
  brands: Array<{ brandId: string; displayName: string }>;
}) {
  const isGlobal = value === null || value.length === 0;

  return (
    <div className="space-y-2">
      <Label>Applies to brands</Label>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant={isGlobal ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange(null)}
        >
          <Globe className="h-3.5 w-3.5 mr-1" />
          All brands
        </Button>
        <Button
          type="button"
          variant={isGlobal ? 'outline' : 'default'}
          size="sm"
          onClick={() => onChange(value && value.length > 0 ? value : [])}
        >
          <Boxes className="h-3.5 w-3.5 mr-1" />
          Specific brands
        </Button>
      </div>
      {!isGlobal && (
        <Card>
          <CardContent className="py-3">
            {brands.length === 0 ? (
              <div className="text-xs text-muted-foreground">No brands available.</div>
            ) : (
              <div className="grid grid-cols-2 gap-1.5 max-h-[200px] overflow-y-auto">
                {brands.map((b) => {
                  const checked = value?.includes(b.brandId) ?? false;
                  return (
                    <button
                      type="button"
                      key={b.brandId}
                      onClick={() =>
                        onChange(
                          checked
                            ? (value ?? []).filter((x) => x !== b.brandId)
                            : [...(value ?? []), b.brandId],
                        )
                      }
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded border text-left text-xs transition-colors',
                        checked
                          ? 'border-primary bg-primary/5 text-foreground'
                          : 'border-input hover:bg-muted/40',
                      )}
                    >
                      {checked ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      )}
                      <span className="font-mono truncate">{b.brandId}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <p className="text-xs text-muted-foreground">
        {isGlobal
          ? 'Toggle applies to every brand a tenant is assigned.'
          : `Toggle applies only to ${(value ?? []).length} selected brand${(value ?? []).length === 1 ? '' : 's'}. Useful for brand-specific controls.`}
      </p>
    </div>
  );
}

// ─── Tenant preview ───────────────────────────────────────────────────────

function TenantTogglePreview({
  label,
  description,
  defaultState,
}: {
  label: string;
  description: string;
  defaultState: boolean;
}) {
  const [previewState, setPreviewState] = useState(defaultState);

  // Keep preview state synced with defaultState when it changes upstream.
  useMemo(() => {
    setPreviewState(defaultState);
  }, [defaultState]);

  return (
    <div className="rounded-md border bg-background p-4 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{label}</div>
          <div className="text-xs text-muted-foreground mt-1">{description}</div>
        </div>
        <Switch checked={previewState} onCheckedChange={setPreviewState} />
      </div>
      <Separator />
      <div className="text-[11px] text-muted-foreground flex items-center gap-1">
        <ChevronRight className="h-3 w-3" />
        Tenant view (Trust → Controls)
      </div>
    </div>
  );
}

// ─── Misc ─────────────────────────────────────────────────────────────────

function EmptyState({ hasFilters, onCreate }: { hasFilters: boolean; onCreate: () => void }) {
  if (hasFilters) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No toggles match the current filters.
      </div>
    );
  }
  return (
    <div className="p-8 text-center space-y-3">
      <ToggleRight className="h-10 w-10 text-muted-foreground mx-auto" />
      <h3 className="text-base font-semibold">No Trust toggles yet</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        Authoring a Trust toggle exposes a sysadmin-controlled wrapper function to
        tenants as a friendly on/off control. v1 ships three toggles in the seed
        (PII detection, strict refusal, citation enforcement).
      </p>
      <Button onClick={onCreate}>
        <Plus className="h-4 w-4 mr-1" />
        Create your first toggle
      </Button>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error && <div className="text-xs text-destructive">{error}</div>}
      {!error && hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
